import React from 'react';

// Shared by the schedule Forms panel (StaffRecommend) and the Service Logs
// lookup. FITZPATRICK_QUESTIONS lives here because the viewer needs the
// question labels to render saved answers.
export const FITZPATRICK_QUESTIONS = [
  { key: 'eyeColor', label: 'Eye Color', options: ['Light Blue, Gray or Green', 'Blue, Green or Hazel', 'Hazel or Light Brown', 'Dark Brown', 'Brown Black'] },
  { key: 'hairColor', label: 'Natural Hair Color', options: ['Red or Sandy Red', 'Blonde', 'Dark Blonde, Chestnut or Brown', 'Dark Brown', 'Black'] },
  { key: 'skinColor', label: 'Skin Color (non-exposed areas)', options: ['Reddish', 'Very Pale', 'Pale with Beige Tint', 'Light Brown', 'Dark Brown'] },
  { key: 'freckles', label: 'Freckles (non-exposed areas)', options: ['Many', 'Several', 'Few', 'Incidental', 'None'] },
  { key: 'sunReaction', label: 'How Does Your Skin React to Sun?', options: ['Painful Redness, Blistering, Peeling', 'Blistering Followed by Peeling', 'Burns Sometimes Followed by Peeling', 'Rare Burns', 'Never Had a Burn'] },
  { key: 'tanning', label: 'Degree of Tanning', options: ['Hardly at All', 'Light Color Tan', 'Reasonable Tan', 'Tan Very Easily', 'Turn Dark Brown'] },
  { key: 'brownAfterSun', label: 'Do You Turn Brown After Sun?', options: ['Hardly at All', 'Seldom', 'Sometimes', 'Often', 'Always'] },
  { key: 'faceReaction', label: 'Face Reaction to Sun', options: ['Very Sensitive', 'Sensitive', 'Normal', 'Very Resistant', 'Never Had a Problem'] },
  { key: 'lastExposure', label: 'Last Sun Exposure', options: ['Less Than 2 Weeks Ago', 'Less Than 1 Month Ago', '1-2 Months Ago', '2-3 Months Ago', 'More Than 3 Months Ago'] },
  { key: 'exposureFrequency', label: 'Area Sun Exposure Frequency', options: ['Never', 'Hardly Ever', 'Sometimes', 'Often', 'Always'] },
];

export default function TreatmentLogViewer({ data }) {
  const labelStyle = { fontSize: 11, fontWeight: 700, color: '#999', letterSpacing: 0.3, marginBottom: 3 };
  const valStyle = { fontSize: 14, color: '#333', fontWeight: 500 };
  const rowStyle = { padding: '10px 0', borderBottom: '1px solid #f0f0f0' };
  const pillStyle = { fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 8, background: '#f0e8f0', color: '#6a3d7d', display: 'inline-block', marginRight: 6, marginTop: 4 };

  const Row = ({ label, value }) => value ? <div style={rowStyle}><div style={labelStyle}>{label}</div><div style={valStyle}>{value}</div></div> : null;
  const Pills = ({ label, items }) => items?.length > 0 ? (
    <div style={rowStyle}><div style={labelStyle}>{label}</div>{items.map(a => <span key={a} style={pillStyle}>{a}</span>)}</div>
  ) : null;

  // Tinting form
  if (data.serviceArea) {
    return (
      <div>
        <Pills label="SERVICE AREA" items={data.serviceArea} />
        <Row label="WHAT THE CLIENT ASKED FOR" value={data.clientAsked} />
        <Pills label="TINT COLOUR — THUYA" items={data.thuyaColors?.map(c => `Thuya - ${c}`)} />
        <Pills label="TINT COLOUR — BERRYWELL" items={data.berrywellColors?.map(c => `Berrywell - ${c}`)} />
        <Row label="MIXING FORMULAS" value={data.mixingFormulas} />
        <Row label="PROCESSING TIME NOTES" value={data.processingNotes} />
      </div>
    );
  }

  // Lash lift form
  if (data.shieldColour !== undefined) {
    return (
      <div>
        <Row label="SHIELD COLOUR" value={data.shieldColour} />
        <Row label="SHIELD SIZE" value={data.shieldSize} />
        <Row label="PERM TIME" value={data.permTime} />
        <Row label="SET TIME" value={data.setTime} />
        <Row label="TINT COLOUR" value={data.tintColour} />
        <Row label="NOTES" value={data.notes} />
      </div>
    );
  }

  // Brow lam form (no shieldColour, has permTime)
  if (data.permTime !== undefined) {
    return (
      <div>
        <Row label="PERM TIME" value={data.permTime} />
        <Row label="SET TIME" value={data.setTime} />
        <Row label="TINT COLOUR" value={data.tintColour} />
        <Row label="NOTES" value={data.notes} />
      </div>
    );
  }

  // Fitzpatrick form
  if (data.answers && data.score !== undefined) {
    return (
      <div>
        {FITZPATRICK_QUESTIONS.map(q => {
          const answerIdx = data.answers[q.key];
          return answerIdx !== undefined ? (
            <div key={q.key} style={rowStyle}>
              <div style={labelStyle}>{q.label.toUpperCase()}</div>
              <div style={valStyle}>{q.options[answerIdx]}</div>
            </div>
          ) : null;
        })}
        <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 10, background: '#f5f0fa', border: '1.5px solid #d5c8e0' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#6a3d7d' }}>Score: {data.score}</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#333', marginTop: 2 }}>{data.skinType}</div>
        </div>
      </div>
    );
  }

  // Laser form
  if (data.sections) {
    return (
      <div>
        {data.fitzpatrick && (
          <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 10, background: '#f5f0fa', border: '1.5px solid #d5c8e0' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6a3d7d', textTransform: 'uppercase', letterSpacing: 0.3 }}>Fitzpatrick Skin Score</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#333', marginTop: 2 }}>{data.fitzpatrick.skinType} (Score: {data.fitzpatrick.score})</div>
          </div>
        )}
        {data.sections.map((sec, idx) => (
          <div key={idx} style={{ padding: '10px 0', borderBottom: idx < data.sections.length - 1 ? '2px dashed #e8e0e4' : 'none' }}>
            <div style={{ ...labelStyle, color: '#d4899e', fontSize: 12, marginBottom: 6 }}>TREATMENT AREA {idx + 1}</div>
            <Row label="AREA" value={sec.area} />
            <Pills label="SPOT SITE" items={sec.spotSite} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              <Row label="J/MS²" value={sec.jMs2} />
              <Row label="MS" value={sec.ms} />
              <Row label="PULSES" value={sec.pulses} />
            </div>
            <Pills label="SKIN TYPE" items={sec.skinType} />
            <Pills label="HAIR COLOR" items={sec.hairColor} />
            <Pills label="COARSENESS" items={sec.coarseness} />
            <Pills label="HAIR DENSITY" items={sec.density} />
          </div>
        ))}
        <Row label="REACTION" value={data.reaction} />
        <Row label="NOTES" value={data.notes} />
      </div>
    );
  }

  // Fallback — render any key/value pairs
  return (
    <div>
      {Object.entries(data).map(([k, v]) => (
        <div key={k} style={rowStyle}>
          <div style={labelStyle}>{k.replace(/([A-Z])/g, ' $1').toUpperCase()}</div>
          <div style={valStyle}>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</div>
        </div>
      ))}
    </div>
  );
}
